import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'

import { SortDirection } from '@brighterly/lib-core-types/options/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { listFinishedBookingRequestStatuses } from '@shared/lib/booking-status'
import { useCsRoutes } from '@shared/api'
import { pick } from '@shared/lib/collections'

import { useBookingStore } from '../model/store/store'

dayjs.extend(utc)
dayjs.extend(timezone)

interface FinishedBookingsApiPayload {
  disciplineId: number
  background: boolean
}
interface FinishedBookingsApiOptions {
  onStart?: () => void
  onSuccess?: (bookings: Booking[]) => void
  onFinish?: () => void
}

export const useFinishedBookingsApi = async (payload: FinishedBookingsApiPayload, options?: FinishedBookingsApiOptions): Promise<Booking[] | null> => {
  const { $axios } = useNuxtApp()

  const url = useCsRoutes('bookings', pick({
    '[discipline_id]': payload.disciplineId,
    status: listFinishedBookingRequestStatuses,
    with_delayed_canceled: 1,
    per_page: 32,
    sort_by: ['start'],
    sort_direction: SortDirection.Desc,
  }))

  const bookingStore = useBookingStore()

  options?.onStart?.()

  const isRefresh = payload.background && bookingStore.getFinishedStatus(payload.disciplineId) === 'ready'
  const requestId = bookingStore.beginFinished(payload.disciplineId, isRefresh ? 'refresh' : 'enter')

  try {
    const { data } = await $axios.get(url!)
    const bookings = data.data?.data || []

    bookingStore.settleFinished(payload.disciplineId, requestId, bookings)

    options?.onSuccess?.(bookings)
    return bookings
  } catch (err) {
    console.error(err)

    bookingStore.failFinished(payload.disciplineId, requestId)
    return null
  } finally {
    options?.onFinish?.()
  }
}
